import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
} from "@mui/material";
import {
  Refresh,
  Undo,
  CheckCircle,
  Error,
  Pending,
  Info,
  SwapHoriz,
} from "@mui/icons-material";
import {
  getDeployments,
  rollbackDeployment,
} from "../../redux/slices/migrationSlice";

const SiteMigrationsTab = ({ site, siteId }) => {
  const dispatch = useDispatch();
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [selectedDeployment, setSelectedDeployment] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const {
    deployments,
    deploymentsLoading,
    deploymentsError,
    pagination,
    rollbackLoading,
  } = useSelector((state) => state.migration);

  useEffect(() => {
    if (siteId) {
      loadDeployments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, page, rowsPerPage]);

  const loadDeployments = () => {
    dispatch(
      getDeployments({
        siteId,
        page: page + 1,
        limit: rowsPerPage,
      }),
    );
  };

  const handleRefresh = () => {
    loadDeployments();
  };

  const handleRollbackClick = (deployment) => {
    setSelectedDeployment(deployment);
    setRollbackDialogOpen(true);
  };

  const handleRollbackConfirm = async () => {
    try {
      await dispatch(rollbackDeployment(selectedDeployment.id)).unwrap();
      setSuccessMessage("Rollback completed successfully!");
      setRollbackDialogOpen(false);
      setSelectedDeployment(null);
      loadDeployments();
      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      setErrorMessage(error || "Rollback failed");
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "SUCCESS":
        return <CheckCircle color="success" />;
      case "FAILED":
        return <Error color="error" />;
      case "ROLLED_BACK":
        return <Undo color="warning" />;
      case "IN_PROGRESS":
        return <Pending color="info" />;
      default:
        return <Pending />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "SUCCESS":
        return "success";
      case "FAILED":
        return "error";
      case "ROLLED_BACK":
        return "warning";
      case "IN_PROGRESS":
        return "info";
      default:
        return "default";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDuration = (startedAt, completedAt) => {
    if (!completedAt) return "In progress...";
    const start = new Date(startedAt);
    const end = new Date(completedAt);
    const diffSeconds = Math.floor((end - start) / 1000);
    if (diffSeconds < 60) return `${diffSeconds}s`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    const remainingSeconds = diffSeconds % 60;
    return `${diffMinutes}m ${remainingSeconds}s`;
  };

  if (deploymentsLoading && deployments.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}
      {deploymentsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {deploymentsError}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Environment Migrations
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Track migrations between SANDBOX → STAGING → PRODUCTION
              </Typography>
            </Box>
            <Tooltip title="Refresh">
              <IconButton
                onClick={handleRefresh}
                disabled={deploymentsLoading}
                size="small"
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          {deployments.length === 0 ? (
            <Alert severity="info" icon={<Info />}>
              <Typography variant="body2">
                No migrations found. Use the "Migrate Environment" button in
                Quick Actions to migrate your site between environments.
              </Typography>
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Status</TableCell>
                      <TableCell>Migration Path</TableCell>
                      <TableCell>Started</TableCell>
                      <TableCell>Duration</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {deployments.map((deployment) => (
                      <TableRow key={deployment.id} hover>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            {getStatusIcon(deployment.status)}
                            <Chip
                              label={deployment.status}
                              color={getStatusColor(deployment.status)}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={deployment.fromEnv}
                              size="small"
                              variant="outlined"
                            />
                            <SwapHoriz fontSize="small" color="action" />
                            <Chip
                              label={deployment.toEnv}
                              size="small"
                              color="primary"
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(deployment.startedAt)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getDuration(
                              deployment.startedAt,
                              deployment.completedAt,
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {deployment.status === "SUCCESS" && (
                            <Tooltip title="Rollback this migration">
                              <Button
                                size="small"
                                startIcon={<Undo />}
                                onClick={() => handleRollbackClick(deployment)}
                                disabled={rollbackLoading}
                                color="warning"
                              >
                                Rollback
                              </Button>
                            </Tooltip>
                          )}
                          {deployment.status === "ROLLED_BACK" && (
                            <Chip
                              label="Rolled Back"
                              size="small"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={pagination.totalCount || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Rollback Confirmation Dialog */}
      <Dialog
        open={rollbackDialogOpen}
        onClose={() => !rollbackLoading && setRollbackDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Undo color="warning" />
            <Typography variant="h6">Confirm Rollback</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedDeployment && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>
                  This action will:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>
                    Remove all containers from{" "}
                    <strong>{selectedDeployment.toEnv}</strong> environment
                  </li>
                  <li>
                    Restore site to{" "}
                    <strong>{selectedDeployment.fromEnv}</strong> environment
                  </li>
                  <li>Delete the target environment configuration</li>
                  <li>Update deployment status to ROLLED_BACK</li>
                </Typography>
              </Alert>

              <Typography variant="body2" color="text.secondary">
                <strong>Migration Path:</strong> {selectedDeployment.fromEnv} →{" "}
                {selectedDeployment.toEnv}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Completed:</strong>{" "}
                {formatDate(selectedDeployment.completedAt)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setRollbackDialogOpen(false)}
            disabled={rollbackLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRollbackConfirm}
            disabled={rollbackLoading}
            startIcon={
              rollbackLoading ? <CircularProgress size={20} /> : <Undo />
            }
          >
            {rollbackLoading ? "Rolling Back..." : "Confirm Rollback"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SiteMigrationsTab;
